<template>
  <div>
    <div class="intro">
      <p>
        您想要了解公司目前在ESG的表現嗎?
        歡迎使用我們的企業健檢工具，簡單快速幫您診斷現階段ESG的表現。
      </p>
    </div>
    <CForm @submit.prevent="submit" class="question-section">
      <h2>ESG企業健檢</h2>
      <section
        :id="question.name"
        v-for="question of questions"
        :key="question.name"
      >
        <h3>
          {{ question.title }}
          <CBadge color="primary">{{"單選 Select one"}}</CBadge>
        </h3>
        <CRow>
          <CCol
            v-for="option of question.options"
            :key="option.value"
            v-bind="question.col"
          >
            <CFormCheck
              type="radio"
              :name="question.name"
              :id="option.label"
              :label="option.label"
              :value="option.value"
              @change="question.value = $event.target.value"
              required
              feedbackInvalid="You must agree before submitting."
            />
          </CCol>
        </CRow>
      </section>
      <section id="question10">
        <h3>10.請問貴公司在以下哪些議題需要優先關注?</h3>
        <div
          class="sort-description d-flex justify-content-between align-items-center"
        >
          <span
            @click="reset"
            class="reset d-flex justify-content-between align-items-center"
          >
            <span>{{"重新選擇 Select again"}}</span>
            <img class="loop-circular" src="@/assets/icons/loop-circular.svg" />
          </span>
        </div>
        <CRow class="mb-5">
          <CCol md="5">
            <div class="drag-sort h-100">
              <div class="drag-sort-title">{{"請依據排序順序點擊選項 Click to arrange in the order of preference"}}</div>
              <draggable
                class="drag-sort-option-wrapper"
                :list="sortOptions"
                group="question10"
                itemKey="value"
              >
              <template #item="{ element }">
                <div @click="() => add(element)" class="drag-sort-option">
                  {{ element.label }}
                </div>
              </template>
            </draggable>
            </div>
          </CCol>
          <CCol
            class="d-flex justify-content-center align-items-center h-full"
            md="2"
          >
            <img src="@/assets/icons/cross-arrow.svg" />
          </CCol>
          <CCol md="5">
            <div class="drag-sort h-100">
              <div class="drag-sort-title">{{"按住選項可拖曳調整排序順序 Drag and drop to reorder options"}}</div>
              <draggable
                class="drag-sort-option-wrapper"
                :list="sortSelections"
                :group="groupOption"
                itemKey="value"
              >
                <template #item="{ element }">
                  <div @click="() => remove(element)" class="drag-sort-option">
                    {{ element.label }}
                  </div>
                </template>
              </draggable>
            </div>
          </CCol>
        </CRow>
      </section>
      <div class="d-flex justify-content-center">
        <CButton type="submit" class="submit-button text-white" color="primary"
          >完成填寫</CButton
        >
      </div>
    </CForm>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import { computed, ref } from '@vue/runtime-core'
import useRadioQuestions from './useRadioQuestions.js'
import useSortQuestion from './useSortQuestion.js'
import { useRouter } from 'vue-router'

export default {
  name: 'PreTestForm',
  components: {
    draggable,
  },
  emits: ['submit'],
  setup(props, { emit }) {
    const router = useRouter()
    const { questions } = useRadioQuestions()
    const {
      REQUIRED_SELECTION_AMOUNT,
      sortOptions,
      sortSelections,
      groupOption,
      add,
      remove,
      reset,
    } = useSortQuestion()
    const isSubmitted = ref(false)

    const isFilled = computed(() => {
      return (
        questions.value.every((question) => question.value) &&
        sortSelections.value.length === REQUIRED_SELECTION_AMOUNT
      )
    })

    const submit = () => {
      if (isSubmitted.value) return
      if (!isFilled) {
        router.push({ path: '/pretest', hash: '#question10' })
        return
      }
      const score = questions.value.reduce((accu, cur) => {
        return (accu += +cur.value)
      }, 0)
      const topic = sortSelections.value.map((v) => v.label).join(' / ')
      emit('submit', { score, topic })
      isSubmitted.value = true

      window.removeEventListener('beforeunload', beforeUnloadListener, {
        capture: true,
      })
    }

    const beforeUnloadListener = (event) => {
      if (questions.value.some((question) => question.value)) {
        event.preventDefault()
        return (event.returnValue = 'Are you sure you want to exit?')
      }
    }

    window.addEventListener('beforeunload', beforeUnloadListener, {
      capture: true,
    })

    return {
      questions,
      sortOptions,
      sortSelections,
      groupOption,
      isFilled,
      add,
      remove,
      reset,
      submit,
    }
  },
}
</script>

<style lang="scss" scoped>
.intro {
  margin-top: 3rem;
  margin-bottom: 2rem;
  font-family: 'Noto Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 32px;
  letter-spacing: 2px;
  color: #4d4d4d;
}
.question-section {
  section {
    margin-top: 34px;
  }
  .row {
    --cui-gutter-y: 16px;
  }
  h2 {
    margin-bottom: 44px;
    font-family: 'Noto Sans';
    font-style: normal;
    font-weight: 500;
    font-size: 20px;
    line-height: 28px;
    letter-spacing: 2px;
    color: #187baf;
  }
  h3 {
    margin-bottom: 18px;
    font-family: 'Noto Sans';
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 28px;
    letter-spacing: 2px;
    color: #4d4d4d;
  }
  .badge {
    margin-left: 10px;
  }
}
.drag-sort {
  .drag-sort-title {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 8px 16px;
    gap: 10px;
    background: #4d4d4d;
    border-radius: 10px 10px 0px 0px;
    font-family: 'Noto Sans';
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 14px;
    letter-spacing: 2px;
    color: #ffffff;
  }
  .drag-sort-option-wrapper {
    height: calc(100% - 30px);
    border-width: 0px 1px 1px 1px;
    border-style: solid;
    border-color: #cccccc;
    border-radius: 0px 0px 10px 10px;
  }
  .drag-sort-option {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 14px 0px;
    padding-left: 50px;
    font-family: 'Noto Sans';
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 2px;
    color: #4d4d4d;
    border-width: 0px 0px 1px 0px;
    border-style: solid;
    border-color: #cccccc;
    border-radius: 0px;
    cursor: pointer;
  }
}
.sort-description {
  margin-bottom: 18px;
  span {
    font-weight: 400;
    font-size: 12px;
    line-height: 20px;
  }
  > span:nth-child(1) {
    color: #187baf;
  }
  .reset {
    cursor: pointer;
    color: #4d4d4d;
  }
  .loop-circular {
    margin-left: 8px;
    width: 16px;
    height: 22px;
  }
}
.submit-button {
  margin-top: 88px;
  margin-bottom: 107px;
  max-width: 456px;
  width: 100%;
}
</style>
